import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import useGlobalStore from '@/stores/global.store';
import useModalStore from '@/stores/modal.store';
import { ModalType } from '@/types/stores/modal';
import { usePollOrderQuery } from './queries/use-poll-order-query';
import { useTimeoutWithControl } from './use-timeout-with-control';
import config from '@/config/index';
import { OrderStatus } from '@/types/api/order';
import { OrderPaymentStatus } from '@/types/api/order/order-payment-status.enum';
import { useCode } from './use-code';

export default function usePollOrderConfirmation() {
  const [polling, setPolling] = React.useState<boolean>(false);
  const statusRef = React.useRef<OrderStatus | undefined>(undefined);

  const openModal = useModalStore((state) => state.open);
  const closeModal = useModalStore((state) => state.close);
  const orderId = useGlobalStore((state) => state.orderId);
  const stopPayment = useGlobalStore((state) => state.stopPayment);
  const code = useCode();
  const navigate = useNavigate();

  const { data: order } = usePollOrderQuery(orderId!, polling, config.app.orderValidation.pollInterval);
  const { start: startTimout, abort: abortTimeout } = useTimeoutWithControl(() => {
    setPolling(false);

    openModal(ModalType.OrderConfirmationFailed, {
      message:
        'Betalningen genomfördes, men det tog för lång tid att bekräfta din beställning. Vänligen kontakta oss på Piggo.se',
    });
  }, config.app.orderValidation.timout);

  React.useEffect(() => {
    // Only relevant once the order has been placed and its payment has actually settled
    // (a Pay Later order never leaves paymentStatus PENDING/UNPAID, so it isn't blocked here).
    if (!order || order.status === OrderStatus.Draft || order.paymentStatus === OrderPaymentStatus.Pending) return;

    statusRef.current = order.status;

    switch (order.status) {
      case OrderStatus.Placed:
        startTimout();
        setPolling(true);
        openModal(ModalType.OrderConfirming);
        break;

      case OrderStatus.Confirmed:
        setPolling(false);
        abortTimeout();
        closeModal();
        stopPayment();
        navigate(`/${code}/confirmation/${orderId}`, { replace: true });
        break;

      default:
        setPolling(false);
        abortTimeout();
        openModal(ModalType.OrderConfirmationFailed, {
          message:
            'Betalningen genomfördes, men ett okänt fel inträffade vid bekräftelse av beställning. Vänligen kontakta oss på Piggo.se',
        });
        break;
    }

    return () => {
      abortTimeout();
      setPolling(false);
    };
  }, [order?.status, order?.paymentStatus, openModal, setPolling]);
}
